import { Stack, Typography } from '@mui/material';
import { GuestJourneyKpiCard } from 'api/reports-service/guest-journey-reports.types';
import DataCard from 'components/data-card/data-card.component';
import MovementIndicator from 'components/movement-indicator/movement-indicator.component';

export type KpiCardProps = {
  /** The BE `PulseKpiCard` payload, or `undefined` while the query is in flight. */
  card?: GuestJourneyKpiCard;
  loading?: boolean;
  'data-testid'?: string;
};

/**
 * Renders a single Guest Journey KPI card from a BE `PulseKpiCard`. The BE owns
 * all presentation — `value` is pre-formatted (rendered as a plain string, no
 * client-side formatting) and every text field is pre-translated. Layout:
 * `title` on top, `value` below it, then `valueDescription` and the `trend`
 * chip stacked under the value, with `footnote` pinned to the bottom.
 */
const KpiCard = ({
  card,
  loading,
  'data-testid': dataTestId,
}: KpiCardProps) => {
  const hasUnderValue = Boolean(card?.valueDescription || card?.trend);

  return (
    <DataCard
      title={card?.title}
      value={card?.value}
      valueType="string"
      loading={loading}
      data-testid={dataTestId}
      slots={{
        description: hasUnderValue ? (
          <Stack spacing={0.5} alignItems="flex-start">
            {card?.valueDescription && (
              <Typography
                variant="body2"
                component="div"
                color="text.secondary"
              >
                {card.valueDescription}
              </Typography>
            )}
            {card?.trend && (
              <MovementIndicator movement={card.trend} size="small" />
            )}
          </Stack>
        ) : undefined,
      }}
    >
      {card?.footnote && (
        <Typography
          variant="caption"
          component="div"
          color="text.secondary"
          data-testid={dataTestId ? `${dataTestId}.Footnote` : undefined}
        >
          {card.footnote}
        </Typography>
      )}
    </DataCard>
  );
};

export default KpiCard;
